/**
 * PHARMAKON — Topbar (design/ux/01_foundations.md §6.1, §6.2; WP-18).
 *
 * In the DOM BEFORE the rail (AppShell): the search is the 2nd/3rd Tab stop
 * (AUD-NAV-07). Left to right:
 *   [≡ < 1024] pharmacy · [Rechercher une page, un produit… Ctrl K] ·
 *   « N alertes stock » / « n lots périmés » · density · offline · display
 *   preferences · notifications · user menu.
 *  - the hamburger (44×44, « Ouvrir le menu ») opens the navigation drawer
 *    below 1024 px;
 *  - the search pill opens the command palette (components/keyboard, WP-17);
 *    it does not claim to search sales or invoices (AUD-NAV-20);
 *  - stock entries: topbar/StockAlertEntries.tsx (cross-module §3.7);
 *  - notifications: topbar/NotificationsPopover.tsx (keyboard, AUD-NAV-22);
 *    ADMIN_PHARMAKON keeps the admin-notification bell;
 *  - user menu: topbar/UserMenu.tsx (APG menu button, AUD-NAV-04).
 */
import * as React from "react";
import { IconButton, Icon, Kbd } from "@/components/ui/primitives";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore } from "@/stores/themeStore.js";
import { useKeyboardStore } from "@/stores/keyboardStore.js";
import OfflineIndicator from "@/offline/OfflineIndicator.jsx";
import AdminNotifBell from "@/components/admin/AdminNotifBell.jsx";
import { StockAlertEntries } from "./topbar/StockAlertEntries";
import { NotificationsPopover } from "./topbar/NotificationsPopover";
import { UserMenu } from "./topbar/UserMenu";

export const Topbar: React.FC = () => {
  const user = useAuthStore((s: any) => s.user);
  const pharmacie = user?.pharmacie ?? null;
  // ADMIN_PHARMAKON has no pharmacie_id: the admin-notification popover
  // replaces the pharmacy feed.
  const isAdmin = user?.role === "ADMIN_PHARMAKON";
  const toggleTweaks = useThemeStore((s: any) => s.toggleTweaks);
  const mobileNavOpen = useThemeStore((s: any) => s.mobileNavOpen);
  const toggleMobileNav = useThemeStore((s: any) => s.toggleMobileNav);
  const density = useThemeStore((s: any) => s.density);
  const setDensity = useThemeStore((s: any) => s.setDensity);
  const openPalette = useKeyboardStore((s) => s.openPalette);

  return (
    <header className="pk-topbar" style={{ gridArea: "topbar" }}>
      {/* Below 1024 px the navigation is a drawer (§6.4): this is its only entry. */}
      <IconButton
        className="pk-mobile-nav-toggle shrink-0"
        icon="menu"
        size="lg"
        label="Ouvrir le menu"
        aria-expanded={mobileNavOpen}
        aria-haspopup="dialog"
        onClick={toggleMobileNav}
      />

      {/* Pharmacy context */}
      <div className="pk-topbar-context">
        <span className="pk-topbar-context-mark" aria-hidden="true">
          <Icon name="cross" size={16} />
        </span>
        <span className="pk-topbar-context-text">
          <span className="pk-topbar-pharmacy">{pharmacie?.nom ?? (isAdmin ? "Plateforme Pharmakon" : "Pharmacie")}</span>
          {(pharmacie?.wilaya || pharmacie?.numero_agrement) && (
            <span className="pk-topbar-pharmacy-sub">
              {pharmacie?.wilaya}
              {pharmacie?.numero_agrement ? (
                <span className="font-mono">{` · n° ${pharmacie.numero_agrement.slice(-8)}`}</span>
              ) : null}
            </span>
          )}
        </span>
      </div>

      {/* Search → command palette (Ctrl+K) */}
      <button
        type="button"
        onClick={openPalette}
        aria-label="Rechercher une page, un produit (Ctrl K)"
        aria-keyshortcuts="Control+K"
        className="pk-topbar-search"
      >
        <Icon name="search" size={18} className="shrink-0" />
        <span className="pk-topbar-search-text">Rechercher une page, un produit…</span>
        <Kbd className="pk-topbar-search-kbd">Ctrl K</Kbd>
      </button>

      <div className="pk-topbar-end">
        <StockAlertEntries />

        <span aria-hidden="true" className="pk-topbar-divider" />

        {/* Density (Confortable ⇄ Compacte) — presentational, per user */}
        <IconButton
          icon={density === "compact" ? "rows-3" : "rows-2"}
          label={
            density === "compact"
              ? "Densité : compacte — passer en confortable"
              : "Densité : confortable — passer en compacte"
          }
          aria-pressed={density === "compact"}
          className="pk-topbar-optional"
          onClick={() => setDensity(density === "compact" ? "comfortable" : "compact")}
        />

        {/* Module 09 — offline status pill + outbox (renders nothing unless
            the deployment flag is on AND this terminal is enrolled). */}
        <OfflineIndicator />

        {/* Below 768 px both stay one step away: « Préférences d'affichage » in the user menu. */}
        <IconButton
          icon="sliders-horizontal"
          label="Préférences d'affichage"
          className="pk-topbar-optional"
          onClick={toggleTweaks}
        />

        {isAdmin ? <AdminNotifBell /> : <NotificationsPopover />}

        <span aria-hidden="true" className="pk-topbar-divider" />

        <UserMenu />
      </div>
    </header>
  );
};

export default Topbar;
